import { supabase } from '@/lib/supabase';
import type {
  AuthResponse,
  AuthError,
  LoginCredentials,
  PasswordResetRequest,
  PasswordUpdate,
  UserProfile,
  FailedLoginAttempt,
} from '../types/auth.types';
import { User, Session } from '@supabase/supabase-js';

// Helper to format Supabase errors into our AuthError type
function formatError(error: unknown): AuthError {
  if (error && typeof error === 'object' && 'message' in error) {
    const err = error as { message: string; code?: string; status?: number };
    return {
      message: err.message,
      code: err.code,
      status: err.status,
    };
  }
  return { message: 'An unexpected error occurred' };
}

// Helper to get user-friendly error messages
function getUserFriendlyMessage(error: AuthError): string {
  const errorMessages: Record<string, string> = {
    'Invalid login credentials': 'Invalid email or password. Please try again.',
    'Email not confirmed': 'Please verify your email before signing in.',
    'User not found': 'No account found with this email address.',
    'Password recovery requires an email': 'Please enter your email address.',
    'Email rate limit exceeded': 'Too many attempts. Please try again later.',
  };

  return errorMessages[error.message] || error.message;
}

/**
 * Sign in with email and password
 */
export async function signIn(
  credentials: LoginCredentials
): Promise<AuthResponse<{ user: User; session: Session }>> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    });

    if (error) {
      const authError = formatError(error);
      
      // Track failed login attempt for notification
      await trackFailedLogin({
        email: credentials.email,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      });

      return {
        data: null,
        error: {
          ...authError,
          message: getUserFriendlyMessage(authError),
        },
      };
    }

    return {
      data: { user: data.user, session: data.session },
      error: null,
    };
  } catch (err) {
    return {
      data: null,
      error: formatError(err),
    };
  }
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<AuthResponse> {
  try {
    // First check if we have a session to sign out from
    const { data: { session } } = await supabase.auth.getSession();
    
    // If no session exists, consider it a successful sign out
    if (!session) {
      return { data: null, error: null };
    }

    const { error } = await supabase.auth.signOut();

    if (error) {
      // Log the error but don't necessarily fail
      // Some errors (like network issues) shouldn't prevent local sign out
      console.warn('Supabase signOut error:', error);
      
      // If it's a session not found error, consider it successful
      if (error.message?.includes('session') || error.message?.includes('Session')) {
        return { data: null, error: null };
      }
      
      return { data: null, error: formatError(error) };
    }

    return { data: null, error: null };
  } catch (err) {
    console.error('SignOut exception:', err);
    // Even on exception, return success - the goal is to clear local state
    return { data: null, error: null };
  }
}

/**
 * Request password reset email
 */
export async function requestPasswordReset(
  request: PasswordResetRequest
): Promise<AuthResponse> {
  try {
    // Get the appropriate redirect URL based on environment
    const redirectUrl = getPasswordResetRedirectUrl();

    const { error } = await supabase.auth.resetPasswordForEmail(request.email, {
      redirectTo: redirectUrl,
    });

    if (error) {
      return {
        data: null,
        error: {
          ...formatError(error),
          message: getUserFriendlyMessage(formatError(error)),
        },
      };
    }

    return { data: null, error: null };
  } catch (err) {
    return { data: null, error: formatError(err) };
  }
}

/**
 * Update password (used after reset link clicked)
 */
export async function updatePassword(
  update: PasswordUpdate
): Promise<AuthResponse> {
  try {
    const { error } = await supabase.auth.updateUser({
      password: update.password,
    });

    if (error) {
      return { data: null, error: formatError(error) };
    }

    return { data: null, error: null };
  } catch (err) {
    return { data: null, error: formatError(err) };
  }
}

/**
 * Get the current session
 */
export async function getSession(): Promise<AuthResponse<Session>> {
  try {
    const { data, error } = await supabase.auth.getSession();

    if (error) {
      return { data: null, error: formatError(error) };
    }

    return { data: data.session, error: null };
  } catch (err) {
    return { data: null, error: formatError(err) };
  }
}

/**
 * Get the current user
 */
export async function getCurrentUser(): Promise<AuthResponse<User>> {
  try {
    const { data, error } = await supabase.auth.getUser();

    if (error) {
      return { data: null, error: formatError(error) };
    }

    return { data: data.user, error: null };
  } catch (err) {
    return { data: null, error: formatError(err) };
  }
}

/**
 * Fetch the user profile from the database
 */
export async function fetchUserProfile(): Promise<AuthResponse<UserProfile>> {
  try {
    const { data, error } = await supabase
      .rpc('get_current_user_profile')
      .single();

    if (error) {
      return { data: null, error: formatError(error) };
    }

    return { data: data as UserProfile, error: null };
  } catch (err) {
    return { data: null, error: formatError(err) };
  }
}

/**
 * Track failed login attempts (sends to Netlify function)
 */
async function trackFailedLogin(attempt: FailedLoginAttempt): Promise<void> {
  try {
    // Only track in production/testing, not local development
    const env = import.meta.env.VITE_ENV;
    if (env === 'development') {
      console.warn('Failed login attempt:', attempt.email);
      return;
    }

    await fetch('/.netlify/functions/track-failed-login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(attempt),
    });
  } catch (err) {
    // Silently fail - don't block login flow for tracking errors
    console.error('Failed to track login attempt:', err);
  }
}

/**
 * Get the password reset redirect URL based on environment
 */
function getPasswordResetRedirectUrl(): string {
  const env = import.meta.env.VITE_ENV;
  
  // These should match your Netlify deploy URLs
  const urls: Record<string, string> = {
    development: 'http://localhost:3000/reset-password',
    testing: 'https://testing--nailedit-renos.netlify.app/reset-password',
    production: 'https://nailedit-renos.netlify.app/reset-password',
  };

  return urls[env] || urls.development;
}